import {
  useEffect,
  useState
} from "react";
import {
  useParams,
  useNavigate,
  Link
} from "react-router-dom";
import {
  User,
  UserRound,
  ArrowLeft
} from "lucide-react";

import {
  getStaffById
} from "../api/staffApi";
import {
  getStaffByIdForStaff
} from "../api/staffPortalApi";

// Safe UI imports with fallbacks
import * as UI from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const Button = UI.Button || (({ children, onClick, variant, size, className, leftIcon }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 rounded-xl text-xs font-bold transition-all ${ variant === "ghost" ? "hover:bg-slate-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-200" : "bg-slate-900 text-white hover:bg-slate-800" } ${size === "sm" ? "px-3 py-1.5" : "px-4 py-2"} ${className}`}
  >
    {leftIcon}
    {children}
  </button>
));

const Badge = UI.Badge || (({ children, className }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-extrabold ${className}`}>
    {children}
  </span>
));

// Borderless Card Component
const Card = ({ className, children }) => (
  <div className={`bg-white dark:bg-neutral-900 rounded-2xl ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ title }) => (
  <h2 className="text-xs font-extrabold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-4">
    {title}
  </h2>
);

const DataTable = ({ rows }) => (
  <div className="overflow-hidden rounded-xl">
    <table className="w-full text-left text-xs">
      <tbody className="divide-y divide-slate-50">
        {rows.map(([label, value], idx) => (
          <tr key={idx} className={idx % 2 === 0 ? "bg-white dark:bg-neutral-900" : "bg-neutral-50 dark:bg-neutral-900/40"}>
            <td className="w-1/3 py-2.5 px-3 font-bold text-neutral-400 dark:text-neutral-500">
              {label}
            </td>
            <td className="py-2.5 px-3 font-semibold text-neutral-800 dark:text-neutral-100">
              {value || <span className="text-neutral-300 dark:text-neutral-600 font-normal italic">Not provided</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StaffDetail = ({ isPortal = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const homeUrl = isPortal ? "/staff-home" : "/school-home";
  const staffListUrl = isPortal ? "/staff-panel/staff" : "/staff";

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const data = isPortal
          ? await getStaffByIdForStaff(id)
          : await getStaffById(id);

        setStaff(data);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load staff details");
      } finally {
        setLoading(false);
      }
    };

    loadStaff();
  }, [id, isPortal]);

  const handleBack = () => {
    if (isPortal) {
      navigate("/staff-panel/staff");
    } else {
      navigate("/staff");
    }
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto text-center font-bold text-xs text-neutral-400 dark:text-neutral-500 py-16">
        Loading staff record...
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-rose-50 text-rose-700 p-4 rounded-2xl text-xs font-bold">
          {"Staff member not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6 bg-neutral-50 dark:bg-neutral-950 min-h-screen">
      {/* Top Navigation & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <nav className="mb-1 flex items-center gap-2 text-xs font-extrabold text-neutral-400 dark:text-neutral-500">
            <Link to={homeUrl} className="hover:text-indigo-600 transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to={staffListUrl} className="hover:text-indigo-600 transition-colors">
              Staff Directory
            </Link>
            <span>/</span>
            <span className="text-neutral-800 dark:text-neutral-100">Staff Profile</span>
          </nav>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Staff Overview
          </h1>
        </div>

        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleBack}
            leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
          >
            Back to Staff List
          </Button>
        </div>
      </div>

      {/* Hero Profile Card */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-neutral-900 flex items-center justify-center text-neutral-600 dark:text-neutral-300 shrink-0">
            {staff.gender === "Female" ? (
              <UserRound className="h-8 w-8" />
            ) : (
              <User className="h-8 w-8" />
            )}
          </div>

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                {staff.name}
              </h1>
              <Badge
                className={
                  staff.active
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-rose-50 text-rose-700"
                }
              >
                {staff.active ? "Active" : "Inactive"}
              </Badge>
            </div>

            <p className="text-xs font-bold text-neutral-500 dark:text-neutral-400">
              {staff?.roleId?.roleName || "No Role Assigned"}
            </p>
            <p className="text-[11px] font-mono font-semibold text-neutral-400 dark:text-neutral-500">
              Staff Code: {staff.staffCode}
            </p>
          </div>
        </div>
      </Card>

      {/* Grid Layout for Structured Data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card 1: Basic Information */}
        <Card className="p-6">
          <CardHeader title="Basic Information" />
          <DataTable
            rows={[
              ["Email", staff.email],
              ["Mobile", staff.mobile],
              ["Gender", staff.gender],
              ["Record Created", formatDate(staff.createdAt)],
              ["Address", staff.address],
            ]}
          />
        </Card>

        {/* Card 2: Employment Information */}
        <Card className="p-6">
          <CardHeader title="Employment Information" />
          <DataTable
            rows={[
              ["Designation", staff.designation],
              ["Employment Type", staff.employmentType],
              ["Employment Status", staff.employmentStatus],
              ["Joining Date", formatDate(staff.joiningDate)],
            ]}
          />
        </Card>
      </div>

      {/* Additional Info Grid — Admin/Principal only */}
      {staff.additionalInfo && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <CardHeader title="Additional Information" />
            <DataTable
              rows={[
                ["Date of Birth", formatDate(staff.additionalInfo.dateOfBirth)],
                ["Blood Group", staff.additionalInfo.bloodGroup],
              ]}
            />
          </Card>

          {(staff.additionalInfo.emergencyContact?.name ||
            staff.additionalInfo.emergencyContact?.mobile) && (
            <Card className="p-6">
              <CardHeader title="Emergency Contact" />
              <DataTable
                rows={[
                  ["Name", staff.additionalInfo.emergencyContact?.name],
                  ["Relation", staff.additionalInfo.emergencyContact?.relation],
                  ["Mobile", staff.additionalInfo.emergencyContact?.mobile],
                ]}
              />
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffDetail;